/**
 * A record's details and form values, in a drawer on the right -- without leaving the page.
 *
 * ED, 2026-10-28, ON THE CONTRACT OPENED INLINE IN A PROJECT: "When Open the Record is clicked,
 * rather than move off the record, bring up the contract details and form values in a right side
 * drawer." The project page is where the person is working; the record's facts are a glance, not
 * a destination. The full page is still one click away from the drawer's header, so nothing that
 * could be reached before is lost.
 *
 * THE SAME READS THE RECORD PAGE MAKES, not a summary of its own: object_revisions with the -002
 * document for the facts, and the form drawn by FormRenderer from the record's OWN contract with
 * the values object_form_input holds. Read-only here on purpose -- editing a record is the record
 * page's job, with its unsaved-changes guard and its lifecycle buttons; a drawer that let you edit
 * would be a second editor with none of that.
 */
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {Box, CircularProgress, Drawer, IconButton, Typography} from "@mui/material";
import {CloseOutlined, OpenInNewOutlined} from "@mui/icons-material";

import {Blue3, Grey2, Grey5, Grey7, Grey8, Grey17, White} from "../../theme/styles";
import {formatLocal} from "../../util/Formatters";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {statusPill} from "../../util/statusPill";
import Attachments from "../Common/Attachments";
import FormRenderer from "../Forms/FormRenderer";
import ParkourDocuments from "./ParkourDocuments";

type Props = {
    /** The REVISION to show. Null closes the drawer. */
    revisionUSID: string | null;
    sessionContext: any;
    onClose: () => void;
};

function Fact({label, value}: {label: string; value: any}) {
    const rendered = React.isValidElement(value);
    const empty = value === null || value === undefined || value === "";
    return (
        <div style={{display: "flex", flexDirection: "column", gap: 1, minWidth: 150}}>
            <Typography style={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>{label}</Typography>
            {rendered ? (
                <div style={{display: "flex", alignItems: "center", minHeight: 21}}>{value}</div>
            ) : (
                <Typography style={{fontSize: 14, color: empty ? Grey7 : Grey17, lineHeight: 1.45,
                                    overflowWrap: "anywhere"}}>
                    {empty ? "—" : String(value)}
                </Typography>
            )}
        </div>
    );
}

export default function RecordDrawer({revisionUSID, sessionContext, onClose}: Props) {
    const navigate = useNavigate();
    const [record, setRecord] = useState<any>(null);
    const [values, setValues] = useState<Record<string, any> | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const isProject = record?.ObjectClassification === "Project";

    const held = record?.ListOfObjectRelationship?.ObjectRelationship;
    const edges: any[] = held == null ? [] : (Array.isArray(held) ? held : [held]);
    const customerEdge = edges.find(one => one?.ObjectRelationshipTypeName === "Contract Customer") ?? null;
    const orders = edges.filter(one => one?.ObjectRelationshipTypeName === "Contract Purchase Order");

    useEffect(() => {
        if (!revisionUSID) { setRecord(null); setValues(null); setProblem(null); return; }
        let cancelled = false;
        setLoading(true);
        const api = makeCresAxiosFoundationAPI(sessionContext);
        Promise.all([
            api.get("t1/v1/object_revisions", {params: {USID: revisionUSID},
                                                headers: {"idoc-response": "v1.ObjectRevision-002"}}),
            // THE ANSWERS, SEPARATELY, and a record with no form is not a failure: the form section
            // then says so rather than the whole drawer refusing to open.
            api.get("t1/v1/object_form_input", {params: {ObjectRevisionUSID: revisionUSID}})
               .catch(() => null),
        ]).then(([revision, form]: any[]) => {
            if (cancelled) return;
            const list = revision?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
            const rows = Array.isArray(list) ? list : [list];
            setRecord(rows[0] ?? null);
            setValues(form ? (form?.data?.Values ?? {}) : null);
            setProblem(rows[0] ? null : "That record could not be opened.");
            setLoading(false);
        }).catch((failure: any) => {
            if (cancelled) return;
            setProblem(failure?.response?.data?.Cause ?? "That record could not be opened.");
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [revisionUSID, sessionContext]);

    return (
        <Drawer anchor="right" open={Boolean(revisionUSID)} onClose={onClose}
                slotProps={{paper: {sx: {width: "min(620px, 92vw)", backgroundColor: White}}}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "10px", padding: "14px 18px",
                      borderBottom: `1px solid ${Grey2}`}}>
                <Typography sx={{fontSize: 15, fontWeight: 700, color: Grey17}}>
                    {record?.ObjectID ?? (loading ? "…" : "—")}
                </Typography>
                <Typography sx={{fontSize: 13, color: Grey8, flex: 1, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                    {record?.Name ?? ""}
                </Typography>
                {record?.Status && statusPill(record.Status)}
                {/* THE FULL PAGE, STILL ONE CLICK AWAY. /object/{usid} names a revision. */}
                <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer",
                                 display: "flex", alignItems: "center", gap: "4px"}}
                            onClick={() => revisionUSID && navigate(`/object/${revisionUSID}`)}>
                    Full record <OpenInNewOutlined sx={{fontSize: 14}}/>
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <CloseOutlined sx={{fontSize: 18}}/>
                </IconButton>
            </Box>

            <Box sx={{padding: "16px 18px 28px 18px", overflowY: "auto", flex: 1}}>
                {loading && (
                    <Box sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                        <CircularProgress size={16}/>
                        <Typography sx={{fontSize: 13, color: Grey7}}>Reading the record…</Typography>
                    </Box>
                )}
                {problem && !loading && (
                    <Typography sx={{fontSize: 13, color: "#B3261E"}}>{problem}</Typography>
                )}
                {record && !loading && (
                    <>
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey8,
                                         marginBottom: "10px"}}>Details</Typography>
                        {/* A PROJECT IS OPEN OR CLOSED, has one type by design and no revision chain
                            (Ed, 2026-10-29, on its page): those facts would read "Project", "Project",
                            "1" and four dashes here too, so the drawer keeps the page's rule. */}
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: "18px 32px"}}>
                            <Fact label="Object ID" value={record.ObjectID}/>
                            {!isProject && <Fact label="Type" value={record.ObjectClassification}/>}
                            {!isProject && record.ObjectTypeName && (
                                <Fact label={`${record.ObjectClassification} Type`} value={record.ObjectTypeName}/>
                            )}
                            {!isProject && <Fact label="Revision" value={record.DisplayRevision ?? record.Revision}/>}
                            <Fact label="Status" value={statusPill(record.Status)}/>
                            {!isProject && <Fact label="Approval" value={record.ApprovalStatus}/>}
                            {!isProject && <Fact label="Released" value={formatLocal(record.ReleasedDatetime)}/>}
                            {!isProject && <Fact label="Effective" value={formatLocal(record.EffectiveDatetime)}/>}
                            {!isProject && <Fact label="Approved" value={formatLocal(record.ApprovedDatetime)}/>}
                        </Box>
                        <Box sx={{marginTop: "18px"}}>
                            <Fact label="Description" value={record.Description}/>
                        </Box>
                        {/* The customer the agreement is with, off the 'Contract Customer' edge the
                            record document carries -- the same read the record page makes. */}
                        {customerEdge && (
                            <Box sx={{marginTop: "18px"}}>
                                <Fact label="Customer"
                                      value={`${customerEdge.ChildObjectID ? `${customerEdge.ChildObjectID} · ` : ""}${customerEdge.ChildName ?? "—"}`}/>
                            </Box>
                        )}
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: "18px 32px",
                                  marginTop: "18px"}}>
                            <Fact label="Primary Position" value={record.PrimaryPositionName}/>
                            <Fact label="Primary User Group" value={record.PrimaryUserGroupName}/>
                            <Fact label="Primary Work Group" value={record.PrimaryWorkGroupName}/>
                            <Fact label="Created" value={formatLocal(record.CreatedDatetime)}/>
                            <Fact label="Modified" value={formatLocal(record.LastModifiedDatetime)}/>
                        </Box>

                        {/* NO FORM SECTION ON A PROJECT: a folder has no shape, and the renderer
                            says so with a code (CC-RCRD-SHAPE) rather than a form -- which is what
                            the drawer drew for PRJ-00001 on 2026-10-30. */}
                        {!isProject && (<>
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey8,
                                         marginTop: "26px", marginBottom: "4px"}}>Form</Typography>
                        {values === null ? (
                            <Typography sx={{fontSize: 13, color: Grey7}}>
                                This record has no form answers to show.
                            </Typography>
                        ) : (
                            // READ-ONLY, from the record's OWN contract -- the same draw the Form
                            // tab makes, minus the editing. Values given once; nothing changes them.
                            <FormRenderer objectRevisionUSID={revisionUSID!} value={values} readOnly/>
                        )}
                        </>)}

                        {/* THE PURCHASE ORDERS UNDER IT, read-only, off the edges the record
                            document carries. Adding one is the project's or the record page's job,
                            where the chooser lives; a drawer is for reading. */}
                        {orders.length > 0 && (
                            <>
                                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey8,
                                                 marginTop: "26px", marginBottom: "6px"}}>Purchase Orders</Typography>
                                <Box sx={{display: "grid", gridTemplateColumns: "1fr 2fr 1fr",
                                          gap: "6px 12px", border: "1px solid #E4E7EC", borderRadius: "6px",
                                          padding: "10px 12px"}}>
                                    {["ID", "Name", "Status"].map(head => (
                                        <Typography key={head} sx={{fontSize: 11.5, color: Grey5,
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.04em"}}>{head}</Typography>
                                    ))}
                                    {orders.map((edge: any) => (
                                        <React.Fragment key={edge.USID}>
                                            <Typography sx={{fontSize: 13, color: Grey17}}>{edge.ChildObjectID ?? "—"}</Typography>
                                            <Typography sx={{fontSize: 13, color: Grey17}}>{edge.ChildName ?? "—"}</Typography>
                                            <Typography sx={{fontSize: 13, color: Grey17}}>{edge.ChildStatus ?? "—"}</Typography>
                                        </React.Fragment>
                                    ))}
                                </Box>
                            </>
                        )}

                        {/* ITS FILES, read-only: Ed, 2026-11-03, on a record opened from a folder -- "show the object
                            detail and form values and attachments in a right side drawer". The component the record
                            page and the review drawer mount, so a file previews and downloads the same way here. */}
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey8,
                                         marginTop: "26px", marginBottom: "4px"}}>Attachments</Typography>
                        <Box>
                            <Attachments sourceUSID={revisionUSID!} readOnly recordLabel={record.ObjectID ?? undefined}
                                         bundleName={record.ObjectID ?? undefined}/>
                        </Box>

                        {/* ITS PARKOUR AI DOCS -- the source product's "Linked Document", which Ed
                            said becomes a Parkour AI Doc. The same component the record's own tab
                            and the project's inline contract mount, keyed on the same revision. */}
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey8,
                                         marginTop: "26px", marginBottom: "4px"}}>Parkour AI Docs</Typography>
                        <ParkourDocuments objectRevisionUSID={revisionUSID!} sessionContext={sessionContext}/>
                    </>
                )}
            </Box>
        </Drawer>
    );
}
